import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Chip,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';

const SaveFlowModal = ({ 
  open, 
  onClose, 
  plan, 
  originalQuery, 
  onSaveSuccess 
}) => {
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savedFlow, setSavedFlow] = useState(null);

  const handleSave = async () => {
    if (!plan || !originalQuery) {
      setError('Missing plan or query data');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/v1/flows/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          original_query: originalQuery,
          description: description.trim() || null,
          plan: plan,
          is_public: isPublic
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save flow');
      }

      const result = await response.json();
      setSavedFlow(result);
      
      if (onSaveSuccess) {
        onSaveSuccess(result);
      }
      
    } catch (err) {
      setError(err.message || 'Failed to save reasoning flow');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setDescription('');
      setIsPublic(true);
      setError(null);
      setSavedFlow(null);
      onClose();
    }
  };

  const renderSaveForm = () => (
    <>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Save this reasoning flow to help other users with similar queries. 
          The system will automatically generate relevant tags.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Original Query
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              p: 2, 
              bgcolor: 'grey.50', 
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200'
            }}
          >
            {originalQuery}
          </Typography>
        </Box>

        <TextField
          fullWidth
          label="Description (Optional)"
          placeholder="Describe when this reasoning flow would be useful..."
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 3 }}
          helperText="Provide context about when this flow is most applicable"
        />

        <FormControlLabel
          control={
            <Switch
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body2">
                Make this flow public
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isPublic 
                  ? 'Other users can discover and use this flow'
                  : 'Only you can see and use this flow'
                }
              </Typography>
            </Box>
          }
          sx={{ mb: 2 }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Flow'}
        </Button>
      </DialogActions>
    </>
  );

  const renderSuccessMessage = () => (
    <>
      <DialogContent>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <AutoAwesomeIcon 
            sx={{ 
              fontSize: 48, 
              color: 'success.main', 
              mb: 2 
            }} 
          />
          <Typography variant="h6" sx={{ mb: 2 }}>
            Flow Saved Successfully!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your reasoning flow has been saved and will be available for recommendations.
          </Typography>

          {savedFlow?.generated_tags && savedFlow.generated_tags.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Generated Tags:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                {savedFlow.generated_tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
        <Button
          variant="contained"
          onClick={handleClose}
        >
          Done
        </Button>
      </DialogActions>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Typography variant="h6" component="div">
          {savedFlow ? 'Flow Saved!' : 'Save Reasoning Flow'}
        </Typography>
        <IconButton
          onClick={handleClose}
          disabled={loading}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {savedFlow ? renderSuccessMessage() : renderSaveForm()}
    </Dialog>
  );
};

export default SaveFlowModal;
